import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const VIEWS = ["day", "week", "month"];
const PRIORITIES = ["low", "medium", "high"];
const CATEGORIES = {
  personal: { name: "Personal", icon: "👤", color: "#8B5CF6" },
  work: { name: "Work", icon: "💼", color: "#3B82F6" },
  health: { name: "Health", icon: "❤️", color: "#EF4444" },
  social: { name: "Social", icon: "👥", color: "#F59E0B" },
  learning: { name: "Learning", icon: "📚", color: "#10B981" },
  "self-care": { name: "Self Care", icon: "✨", color: "#EC4899" },
  finance: { name: "Finance", icon: "💰", color: "#22C55E" },
  spiritual: { name: "Spiritual", icon: "🙏", color: "#6366F1" },
};

const MOODS = [
  { id: "great", emoji: "😄", label: "Great", color: "#22C55E" },
  { id: "good", emoji: "🙂", label: "Good", color: "#84CC16" },
  { id: "okay", emoji: "😐", label: "Okay", color: "#F59E0B" },
  { id: "low", emoji: "😔", label: "Low", color: "#F97316" },
  { id: "bad", emoji: "😢", label: "Bad", color: "#EF4444" },
];

export default function Planner() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("tasks");
  const [view, setView] = useState("week");
  const [tasks, setTasks] = useState([]);
  const [habits, setHabits] = useState([]);
  const [moodEntries, setMoodEntries] = useState([]);
  const [yearlyGoals, setYearlyGoals] = useState({ yearly_goals: [], quarterly_goals: {} });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showHabitModal, setShowHabitModal] = useState(false);
  const [showMoodModal, setShowMoodModal] = useState(false);
  const [showYearlyModal, setShowYearlyModal] = useState(false);
  const [dailyPlan, setDailyPlan] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);

  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    time: "09:00",
    category: "personal",
    priority: "medium",
  });

  const [newHabit, setNewHabit] = useState({
    name: "",
    description: "",
    frequency: "daily",
    category: "health",
  });

  const [newMood, setNewMood] = useState({
    mood: "good",
    energy: "medium",
    notes: "",
    factors: [],
  });

  const [newYearlyGoal, setNewYearlyGoal] = useState({
    title: "",
    description: "",
    category: "life",
    quarter: null,
    milestones: [],
  });

  useEffect(() => {
    fetchData();
  }, [view]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tasksRes, habitsRes, moodRes, yearlyRes, statsRes] = await Promise.all([
        axios.get(`${API}/planner/tasks?view=${view}`),
        axios.get(`${API}/planner/habits`),
        axios.get(`${API}/planner/mood`),
        axios.get(`${API}/planner/yearly-goals`),
        axios.get(`${API}/planner/stats`),
      ]);
      setTasks(tasksRes.data.tasks || []);
      setHabits(habitsRes.data.habits || []);
      setMoodEntries(moodRes.data.entries || []);
      setYearlyGoals(yearlyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching planner data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    try {
      await axios.post(`${API}/planner/tasks`, newTask);
      toast.success("Task created! +2 points");
      setShowTaskModal(false);
      setNewTask({
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        time: "09:00",
        category: "personal",
        priority: "medium",
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const toggleTaskComplete = async (taskId) => {
    try {
      const res = await axios.post(`${API}/planner/tasks/${taskId}/complete`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const createHabit = async (e) => {
    e.preventDefault();
    if (!newHabit.name.trim()) return;
    try {
      await axios.post(`${API}/planner/habits`, newHabit);
      toast.success("Habit created!");
      setShowHabitModal(false);
      setNewHabit({ name: "", description: "", frequency: "daily", category: "health" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create habit");
    }
  };

  const toggleHabitComplete = async (habitId) => {
    try {
      const res = await axios.post(`${API}/planner/habits/${habitId}/complete`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to update habit");
    }
  };

  const logMood = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/planner/mood`, newMood);
      toast.success("Mood logged! +2 points");
      setShowMoodModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to log mood");
    }
  };

  const createYearlyGoal = async (e) => {
    e.preventDefault();
    if (!newYearlyGoal.title.trim()) return;
    try {
      await axios.post(`${API}/planner/yearly-goals`, newYearlyGoal);
      toast.success("Yearly goal created!");
      setShowYearlyModal(false);
      setNewYearlyGoal({ title: "", description: "", category: "life", quarter: null, milestones: [] });
      fetchData();
    } catch (error) {
      toast.error("Failed to create goal");
    }
  };

  const generateDailyPlan = async () => {
    setGeneratingPlan(true);
    try {
      const res = await axios.post(`${API}/planner/generate-daily-plan`);
      setDailyPlan(res.data);
      toast.success("Daily plan generated!");
    } catch (error) {
      toast.error("Failed to generate plan");
    } finally {
      setGeneratingPlan(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const todayTasks = tasks.filter((t) => t.date === today);
  const todaysHabitsCompleted = habits.filter((h) =>
    h.completions?.includes(today)
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      <Navigation />

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Daily Planner</h1>
          <p className="text-gray-600">Plan your day, week, month & year</p>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-amber-600">
                  {stats.tasks.today_completed}/{stats.tasks.today_total}
                </div>
                <div className="text-sm text-gray-500">Today's Tasks</div>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-600">
                  {todaysHabitsCompleted}/{habits.length}
                </div>
                <div className="text-sm text-gray-500">Habits Done</div>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {stats.habits.longest_streak}
                </div>
                <div className="text-sm text-gray-500">Best Streak</div>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {stats.tasks.completion_rate}%
                </div>
                <div className="text-sm text-gray-500">Completion Rate</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {["tasks", "habits", "mood", "yearly", "ai-plan"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all ${
                activeTab === tab
                  ? "bg-amber-500 text-white shadow-md"
                  : "bg-white text-gray-600 hover:bg-amber-100"
              }`}
            >
              {tab === "tasks" && "📋 Tasks"}
              {tab === "habits" && "🔄 Habits"}
              {tab === "mood" && "😊 Mood"}
              {tab === "yearly" && "🎯 Year Goals"}
              {tab === "ai-plan" && "🤖 AI Plan"}
            </button>
          ))}
        </div>

        {/* TASKS TAB */}
        {activeTab === "tasks" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2">
                {VIEWS.map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      view === v
                        ? "bg-amber-500 text-white"
                        : "bg-white text-gray-600"
                    }`}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
              <Button onClick={() => setShowTaskModal(true)} className="bg-amber-500 hover:bg-amber-600">
                + Add Task
              </Button>
            </div>

            {/* Task List */}
            <div className="space-y-3">
              {tasks.length === 0 ? (
                <Card className="bg-white/80">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl mb-2">📋</div>
                    <p className="text-gray-500">No tasks yet. Add your first task!</p>
                  </CardContent>
                </Card>
              ) : (
                tasks.map((task) => (
                  <Card
                    key={task.id}
                    className={`bg-white/90 backdrop-blur border-l-4 ${
                      task.completed ? "opacity-60" : ""
                    }`}
                    style={{ borderLeftColor: CATEGORIES[task.category]?.color || "#D97706" }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggleTaskComplete(task.id)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            task.completed
                              ? "bg-green-500 border-green-500 text-white"
                              : "border-gray-300 hover:border-amber-500"
                          }`}
                        >
                          {task.completed && "✓"}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={task.completed ? "line-through text-gray-400" : "font-medium"}>
                              {task.title}
                            </span>
                            <span
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: CATEGORIES[task.category]?.color + "20",
                                color: CATEGORIES[task.category]?.color,
                              }}
                            >
                              {CATEGORIES[task.category]?.icon} {task.category}
                            </span>
                            {task.priority === "high" && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                                High
                              </span>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-500">{task.description}</p>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {task.date} {task.time && `at ${task.time}`}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* HABITS TAB */}
        {activeTab === "habits" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Daily Habits</h2>
              <Button onClick={() => setShowHabitModal(true)} className="bg-amber-500 hover:bg-amber-600">
                + Add Habit
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {habits.length === 0 ? (
                <Card className="bg-white/80 col-span-2">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl mb-2">🔄</div>
                    <p className="text-gray-500">No habits yet. Start building good habits!</p>
                  </CardContent>
                </Card>
              ) : (
                habits.map((habit) => {
                  const isCompletedToday = habit.completions?.includes(today);
                  return (
                    <Card key={habit.id} className="bg-white/90 backdrop-blur">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleHabitComplete(habit.id)}
                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all ${
                              isCompletedToday
                                ? "bg-green-500 text-white"
                                : "bg-gray-100 hover:bg-amber-100"
                            }`}
                          >
                            {isCompletedToday ? "✓" : CATEGORIES[habit.category]?.icon || "⭐"}
                          </button>
                          <div className="flex-1">
                            <div className="font-medium">{habit.name}</div>
                            <div className="text-sm text-gray-500">
                              🔥 {habit.streak || 0} day streak
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-400">{habit.frequency}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* MOOD TAB */}
        {activeTab === "mood" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Mood Tracker</h2>
              <Button onClick={() => setShowMoodModal(true)} className="bg-amber-500 hover:bg-amber-600">
                + Log Today's Mood
              </Button>
            </div>

            {/* Mood History */}
            <div className="grid gap-4 md:grid-cols-3">
              {moodEntries.length === 0 ? (
                <Card className="bg-white/80 col-span-3">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl mb-2">😊</div>
                    <p className="text-gray-500">Start tracking your mood!</p>
                  </CardContent>
                </Card>
              ) : (
                moodEntries.slice(0, 7).map((entry) => {
                  const moodInfo = MOODS.find((m) => m.id === entry.mood);
                  return (
                    <Card key={entry.id} className="bg-white/90 backdrop-blur">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="text-3xl">{moodInfo?.emoji || "😐"}</div>
                          <div>
                            <div className="font-medium" style={{ color: moodInfo?.color }}>
                              {moodInfo?.label || entry.mood}
                            </div>
                            <div className="text-xs text-gray-400">{entry.date}</div>
                          </div>
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-gray-600">{entry.notes}</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* YEARLY GOALS TAB */}
        {activeTab === "yearly" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{yearlyGoals.year} Goals</h2>
              <Button onClick={() => setShowYearlyModal(true)} className="bg-amber-500 hover:bg-amber-600">
                + Add Goal
              </Button>
            </div>

            {/* Yearly Goals */}
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-3">🎯 Year Goals</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {yearlyGoals.yearly_goals?.length === 0 ? (
                    <Card className="bg-white/80 col-span-2">
                      <CardContent className="p-6 text-center">
                        <p className="text-gray-500">Set your big goals for the year!</p>
                      </CardContent>
                    </Card>
                  ) : (
                    yearlyGoals.yearly_goals?.map((goal) => (
                      <Card key={goal.id} className="bg-white/90 backdrop-blur">
                        <CardContent className="p-4">
                          <div className="font-medium mb-2">{goal.title}</div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                            <div
                              className="bg-amber-500 h-2 rounded-full transition-all"
                              style={{ width: `${goal.progress || 0}%` }}
                            />
                          </div>
                          <div className="text-sm text-gray-500">{goal.progress || 0}% complete</div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>

              {/* Quarterly Goals */}
              {[1, 2, 3, 4].map((q) => (
                <div key={q}>
                  <h3 className="font-semibold text-lg mb-3">Q{q} Goals</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {yearlyGoals.quarterly_goals?.[q]?.length === 0 ? (
                      <p className="text-gray-400 text-sm col-span-2">No Q{q} goals yet</p>
                    ) : (
                      yearlyGoals.quarterly_goals?.[q]?.map((goal) => (
                        <Card key={goal.id} className="bg-white/80">
                          <CardContent className="p-4">
                            <div className="font-medium">{goal.title}</div>
                            <div className="text-sm text-gray-500">{goal.progress || 0}%</div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI PLAN TAB */}
        {activeTab === "ai-plan" && (
          <div>
            <Card className="bg-white/90 backdrop-blur">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  🤖 AI Daily Planner
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Let AI create an optimized daily schedule based on your tasks, habits, and goals.
                </p>
                <Button
                  onClick={generateDailyPlan}
                  disabled={generatingPlan}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  {generatingPlan ? "Generating..." : "✨ Generate Today's Plan"}
                </Button>

                {dailyPlan && (
                  <div className="mt-6 p-4 bg-amber-50 rounded-xl">
                    <h3 className="font-semibold mb-2">Your AI-Generated Plan</h3>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                      {dailyPlan.plan}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">New Task</h2>
              <button onClick={() => setShowTaskModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <form onSubmit={createTask} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Task Title</label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="What needs to be done?"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Optional details..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <Input
                    type="date"
                    value={newTask.date}
                    onChange={(e) => setNewTask({ ...newTask, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Time</label>
                  <Input
                    type="time"
                    value={newTask.time}
                    onChange={(e) => setNewTask({ ...newTask, time: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewTask({ ...newTask, category: key })}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${
                        newTask.category === key
                          ? "text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                      style={{
                        backgroundColor: newTask.category === key ? cat.color : undefined,
                      }}
                    >
                      {cat.icon} {cat.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Priority</label>
                <div className="flex gap-2">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setNewTask({ ...newTask, priority: p })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        newTask.priority === p
                          ? p === "high"
                            ? "bg-red-500 text-white"
                            : p === "medium"
                            ? "bg-amber-500 text-white"
                            : "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600">
                Create Task
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Habit Modal */}
      {showHabitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">New Habit</h2>
              <button onClick={() => setShowHabitModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <form onSubmit={createHabit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Habit Name</label>
                <Input
                  value={newHabit.name}
                  onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
                  placeholder="e.g., Drink 8 glasses of water"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={newHabit.description}
                  onChange={(e) => setNewHabit({ ...newHabit, description: e.target.value })}
                  placeholder="Why is this habit important?"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(CATEGORIES).slice(0, 6).map(([key, cat]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewHabit({ ...newHabit, category: key })}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${
                        newHabit.category === key
                          ? "text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                      style={{
                        backgroundColor: newHabit.category === key ? cat.color : undefined,
                      }}
                    >
                      {cat.icon} {cat.name}
                    </button>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600">
                Create Habit
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Mood Modal */}
      {showMoodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">How are you feeling?</h2>
              <button onClick={() => setShowMoodModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <form onSubmit={logMood} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Mood</label>
                <div className="flex justify-around">
                  {MOODS.map((mood) => (
                    <button
                      key={mood.id}
                      type="button"
                      onClick={() => setNewMood({ ...newMood, mood: mood.id })}
                      className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                        newMood.mood === mood.id
                          ? "bg-amber-100 scale-110"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <span className="text-3xl">{mood.emoji}</span>
                      <span className="text-xs mt-1">{mood.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <Textarea
                  value={newMood.notes}
                  onChange={(e) => setNewMood({ ...newMood, notes: e.target.value })}
                  placeholder="What's on your mind?"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600">
                Log Mood
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Yearly Goal Modal */}
      {showYearlyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">New Yearly Goal</h2>
              <button onClick={() => setShowYearlyModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <form onSubmit={createYearlyGoal} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Goal Title</label>
                <Input
                  value={newYearlyGoal.title}
                  onChange={(e) => setNewYearlyGoal({ ...newYearlyGoal, title: e.target.value })}
                  placeholder="What do you want to achieve?"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={newYearlyGoal.description}
                  onChange={(e) => setNewYearlyGoal({ ...newYearlyGoal, description: e.target.value })}
                  placeholder="Why is this important?"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Quarter (optional)</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewYearlyGoal({ ...newYearlyGoal, quarter: null })}
                    className={`px-4 py-2 rounded-lg ${
                      newYearlyGoal.quarter === null ? "bg-amber-500 text-white" : "bg-gray-100"
                    }`}
                  >
                    Full Year
                  </button>
                  {[1, 2, 3, 4].map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setNewYearlyGoal({ ...newYearlyGoal, quarter: q })}
                      className={`px-4 py-2 rounded-lg ${
                        newYearlyGoal.quarter === q ? "bg-amber-500 text-white" : "bg-gray-100"
                      }`}
                    >
                      Q{q}
                    </button>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600">
                Create Goal
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
